import { Logging, NotificationData } from '@vcd-ecm-ui/Common';
import * as _ from 'lodash';
import {
  GenericGridRowData,
  GridDataNativeType,
  GridFilter,
  GridSort,
} from '../Interfaces';
import {
  ADD_FILTER,
  CLEAR_FILTERS_SORTERS,
  CLEAR_NOTIFICATION,
  DATA_REQUEST_CACHE,
  DATA_REQUEST_SERVICE_FAILED,
  DATA_REQUEST_SERVICE_STARTED,
  DATA_REQUEST_SERVICE_SUCCEEDED,
  DELETE_FILTER,
  ReportActionTypes,
  SET_FILTERS_SORTERS,
  SET_FILTERS_SORTERS_READY,
  SET_NOTIFICATION,
  SET_PAGINATION_FILTERS_SORTERS,
} from './reportActionTypes';

export interface ReportState {
  loading: boolean;
  loadingStart: Date;
  filtersSortersReady: boolean;

  // Live Data
  dataRows: GenericGridRowData[];
  pageNumber: number;
  pageSize: number;
  totalNumberRows: number;
  startRow: number;
  endRow: number;

  // Cache
  useCache: boolean,
  virtualPageSize: number;
  dataRowsCache: GenericGridRowData[];
  cacheStartRow: number;
  cacheEndRow: number;

  // Filters & Sorting
  filters: GridFilter[];
  sorters: GridSort[];

  // Last Filters & Sorting used to query Server
  lastFilters: GridFilter[];
  lastSorters: GridSort[];

  // Notification
  notification: NotificationData;

  // Parser - Parse from Native data Type to GridRowData
  rowParser: (row: GridDataNativeType) => GenericGridRowData;

  // Model
  model: 'server' | 'client' | 'static';

  // Data Type
  dataTypeDescription: string;
}

export const initialReportState: ReportState = {
  loading: false,
  loadingStart: null,
  filtersSortersReady: false,

  // Live Data
  dataRows: null,
  pageNumber: 1,
  pageSize: 25,
  totalNumberRows: null,
  startRow: null,
  endRow: null,

  // Cache
  useCache: true,
  virtualPageSize: null,
  dataRowsCache: null,
  cacheStartRow: null,
  cacheEndRow: null,

  // Filters & Sorters
  filters: null,
  sorters: null,

  // Last Filters & Sorters
  lastFilters: null,
  lastSorters: null,

  // Notification
  notification: null,

  // Parser
  rowParser: null,

  // Model
  model: null,

  // Data Type
  dataTypeDescription: null,
};

export const reportReducer = (state: ReportState = initialReportState, action: ReportActionTypes): ReportState => {
  Logging.Debug('Reducer:', action, state);

  switch (action.type) {
    case DATA_REQUEST_SERVICE_STARTED:
    {
      Logging.Debug('DATA_REQUEST_SERVICE_STARTED');

      return {
        ...state,

        // Loading State
        loading: true,
        loadingStart: new Date(),
      };
    }
    case DATA_REQUEST_SERVICE_SUCCEEDED:
    {
      // Map the rows returned by the Service from Native Data Model to GridRowData
      const dataRows: GenericGridRowData[] = action.dataRows && Array.isArray(action.dataRows)
        ? action.dataRows.map((row) => state.rowParser(row))
        : [];

      Logging.Debug('DATA_REQUEST_SERVICE_SUCCEEDED',
        action.pageNumber, action.pageSize, action.startRow, action.endRow,
        action.cacheStartRow, action.cacheEndRow, action.filters, action.sorters, dataRows.length);

      const loadingEnd: Date = new Date();
      const timeDiff = state.loadingStart ? loadingEnd.getTime() - state.loadingStart.getTime() : null;
      Logging.Info(`API: ${dataRows.length} rows in ${timeDiff} ms`);

      return {
        ...state,
        // Live Data - Calculate the Cache Offsets relative to the cacheStartRow
        // Return the requested sub set of the Cache
        dataRows: state.useCache ? dataRows.slice(action.startRow - action.cacheStartRow, action.endRow - action.cacheStartRow) : dataRows,
        pageNumber: action.pageNumber,
        pageSize: action.pageSize,
        totalNumberRows: action.totalNumberRows,
        startRow: action.startRow,
        endRow: action.endRow,

        // Cache
        cacheStartRow: state.useCache ? action.cacheStartRow : null,
        cacheEndRow: state.useCache ? action.cacheEndRow : null,
        dataRowsCache: state.useCache ? dataRows : null,

        // Filters & Sorting
        filters: _.isEqual(state.filters, action.filters) ? state.filters : action.filters,
        sorters: _.isEqual(state.sorters, action.sorters) ? state.sorters : action.sorters,

        // Filters & Sorting
        lastFilters: action.filters,
        lastSorters: action.sorters,

        // Loading State
        loading: false,
        loadingStart: null,
      };
    }
    case DATA_REQUEST_SERVICE_FAILED:
    {
      Logging.Debug('DATA_REQUEST_SERVICE_FAILED', action.error);

      const notification: NotificationData = {
        type: 'Error',
        title: `${state.dataTypeDescription} Read Error`,
        description: `Unable to read the '${state.dataTypeDescription}'.`,
        error: action.error,
      };
      return {
        ...state,
        dataRows: [],
        notification,

        // Loading State
        loading: false,
        loadingStart: null,
      };
    }
    case DATA_REQUEST_CACHE:
    {
      Logging.Debug('DATA_REQUEST_CACHE',
        action.pageNumber, action.pageSize, action.startRow, action.endRow, state.cacheStartRow, state.cacheEndRow);

      return {
        ...state,
        // Live Data - Calculate the Cache Offsets relative to the cacheStartRow
        // Return the requested sub set of the Cache
        dataRows: state.dataRowsCache.slice(action.startRow - state.cacheStartRow, action.endRow - state.cacheStartRow),
        pageNumber: action.pageNumber,
        pageSize: action.pageSize,
        startRow: action.startRow,
        endRow: action.endRow,

        // Loading State
        loading: false,
        loadingStart: null,
      };
    }
    case SET_NOTIFICATION:
      Logging.Debug('Set Notification');
      return {
        ...state,
        notification: action.notification,
      };
    case CLEAR_NOTIFICATION:
      Logging.Debug('Clear Notification');
      return {
        ...state,
        notification: null,
      };
    case SET_FILTERS_SORTERS:
      Logging.Debug('Set Filters and Sorters:', action.filters, action.sorters);
      return {
        ...state,
        filters: _.isEqual(state.filters, action.filters) ? state.filters : action.filters,
        sorters: _.isEqual(state.sorters, action.sorters) ? state.sorters : action.sorters,
        filtersSortersReady: true,
      };
    case CLEAR_FILTERS_SORTERS:
      Logging.Debug('Clear Filters and Sorters');
      return {
        ...state,
        filters: null,
        sorters: null,
      };
    case SET_PAGINATION_FILTERS_SORTERS:
      Logging.Debug('Set Pagination, Filters and Sorters:', action.pageNumber, action.pageSize, action.filters, action.sorters);
      return {
        ...state,
        pageNumber: action.pageNumber,
        pageSize: action.pageSize,
        filters: _.isEqual(state.filters, action.filters) ? state.filters : action.filters,
        sorters: _.isEqual(state.sorters, action.sorters) ? state.sorters : action.sorters,
        filtersSortersReady: true,
      };
    case ADD_FILTER:
    {
      const filters = state.filters ? _.cloneDeep(state.filters) : [];
      const filter = filters.filter((x) => x.column === action.filter.column)[0];

      Logging.Debug('Add Filter', action.filter, filter);

      // Update the Filter
      if (filter) {
        filter.value = action.filter.value;
      } else {
        filters.push(action.filter);
      }

      return {
        ...state,
        filters,
      };
    }
    case DELETE_FILTER:
    {
      const filters = _.cloneDeep(state.filters);
      const filter = filters ? filters.filter((x) => x.column === action.filter.column)[0] : null;

      // Remove the Filter
      const newFilters = filters ? filters?.filter((x) => x.column !== action.filter.column) : null;

      Logging.Debug('Delete Filter', action.filter, filter);

      return filter
        ? {
          ...state,
          // Check if any more Filters are Left
          filters: newFilters?.length === 0 ? null : newFilters,
        }
        : {
          ...state,
        };
    }
    case SET_FILTERS_SORTERS_READY:
    {
      return {
        ...state,
        filtersSortersReady: true,
      };
    }
    default:
      return state;
  }
};
